import type { LogEntry } from './logs';
import type { ApiClient } from '@nyce/admin-kit/api';
import { client } from '../session';

// ---------- GKE pods (M4, docs/contracts.md) ----------

export interface Container {
  name: string;
  image: string;
  ready: boolean;
  restarts: number;
  state: 'running' | 'waiting' | 'terminated';
  reason: string | null;
  message: string | null;
  lastTermination: { reason: string; exitCode: number; finishedAt: string } | null;
}

export interface Pod {
  namespace: string;
  name: string;
  phase: string;
  ready: number;
  total: number;
  restarts: number;
  node: string;
  podIp: string;
  startTime: string | null;
  createdAt: string;
  deployment: string | null;
  /** kubectl-style: Running | CrashLoopBackOff | OOMKilled | ImagePullBackOff | Pending | Completed | Terminating … */
  status: string;
  containers: Container[];
}

export interface DeploymentCondition {
  type: string;
  status: string;
  reason: string;
  message: string;
}

export interface Deployment {
  namespace: string;
  name: string;
  replicas: number;
  readyReplicas: number;
  updatedReplicas: number;
  availableReplicas: number;
  images: string[];
  paused: boolean;
  pausedReplicas: number | null;
  restartedAt: string | null;
  conditions: DeploymentCondition[];
}

export interface PodList {
  pods: Pod[];
  deployments: Deployment[];
  fetchedAt: string;
}

export interface PodEvent {
  type: string;
  reason: string;
  message: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
  object: string;
}

export interface PodLogQuery {
  environmentId: string;
  namespace: string;
  pod: string;
  container?: string;
  /** ≤ 5000, default 500. */
  tail?: number;
  previous?: boolean;
}

export type MetricsRange = '1h' | '6h' | '24h' | '7d';

/** Usage over time (`t` ISO, `v` cores or bytes) and the container's current request/limit (null = not set). */
export interface MetricSeries {
  points: { t: string; v: number }[];
  request: number | null;
  limit: number | null;
}

export interface PodMetrics {
  range: MetricsRange;
  stepSeconds: number;
  from: string;
  to: string;
  containers: { name: string; cpu: MetricSeries; memory: MetricSeries }[];
  /** Set when the series cover every pod of this deployment (the highest pod per step), not one pod. */
  deployment?: string | null;
}

interface Target {
  environmentId: string;
  namespace: string;
}

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  return q.toString();
};

export function podsApi(c: ApiClient) {
  return {
    list: (environmentId: string, signal?: AbortSignal) =>
      c.get<PodList>(`/pods?${qs({ environmentId })}`, { signal }),
    events: (environmentId: string, namespace: string, pod?: string) =>
      c.get<PodEvent[]>(`/pods/events?${qs({ environmentId, namespace, pod })}`),
    /** CPU (cores) and non-evictable memory (bytes) per container from Cloud Monitoring. */
    /** One pod, or with `{deployment}` every pod that deployment had in the range (restarts and rollouts included). */
    metrics: (
      environmentId: string,
      namespace: string,
      of: { pod: string } | { deployment: string },
      range: MetricsRange,
      signal?: AbortSignal
    ) => c.get<PodMetrics>(`/pods/metrics?${qs({ environmentId, namespace, ...of, range })}`, { signal }),
    /** `text/plain` from the k8s log API. */
    logs: async (q: PodLogQuery, signal?: AbortSignal) => {
      const r = await c.requestRaw(
        'GET',
        `/pods/logs?${qs({ ...q, tail: q.tail ?? 500, previous: q.previous ?? false })}`,
        undefined,
        { accept: 'text/plain', signal }
      );
      return r.text();
    },
    /** The same log parsed into Logs-page entries (timestamps, JSON level/message, severity rescue), newest first. */
    logEntries: (q: PodLogQuery, signal?: AbortSignal) =>
      c.get<{ entries: LogEntry[] }>(
        `/pods/logs?${qs({ ...q, tail: q.tail ?? 500, previous: q.previous ?? false, format: 'entries' })}`,
        { signal }
      ),
    deletePod: (t: Target & { pod: string }) => c.post<{ ok: true }>('/pods/delete', t),
    restart: (t: Target & { deployment: string }) => c.post<{ ok: true }>('/deployments/restart', t),
    scale: (t: Target & { deployment: string; replicas: number }) =>
      c.post<{ ok: true }>('/deployments/scale', t),
    pause: (t: Target & { deployment: string }) => c.post<{ ok: true }>('/deployments/pause', t),
    resume: (t: Target & { deployment: string }) => c.post<{ ok: true }>('/deployments/resume', t),
  };
}

export const pods = podsApi(client);
